import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { CheckCircle, Cancel, MoneyOff, Paid, Person, Search, Star } from '@mui/icons-material'
import { apiService } from '../../services/api'
import { WARZONE_COIN_IMAGE_URL } from '../../properties'

type AdminUser = {
  id?: string
  playerName?: string
  email?: string
  active?: boolean
  eosid?: string
  warzoneCoinSaldo?: number
  vipDiasRestantes?: number
}

type PalworldUsuarioApi = {
  id?: string
  email?: string
  pal_player_name?: string
  ativo?: boolean
  warzone_coin_saldo?: number
  vip_dias_restantes?: number
}

const safeNumber = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

const normalizeUsuarios = (raw: any): AdminUser[] => {
  // Novo endpoint: { success: true, data: [{ id, email, pal_player_name, ativo }] }
  const list = raw?.data?.content ?? raw?.content ?? raw?.data ?? raw
  if (!Array.isArray(list)) return []
  return (list as PalworldUsuarioApi[]).map((u) => ({
    id: u?.id,
    email: u?.email,
    playerName: u?.pal_player_name,
    active: u?.ativo,
    warzoneCoinSaldo: safeNumber(u?.warzone_coin_saldo) ?? undefined,
    vipDiasRestantes: safeNumber(u?.vip_dias_restantes) ?? undefined
  }))
}

// Manter o restante da tela "normal" (sem escurecer) enquanto o modal está aberto.
const vipDialogBackdropSx = { bgcolor: 'transparent' }
const vipDialogPaperSx = {
  bgcolor: '#0c0e1c',
  backgroundImage: 'none',
  border: '1px solid rgba(255,255,255,0.12)',
  boxShadow: '0 24px 90px rgba(0,0,0,0.65)'
}

const AdminUsuariosPage: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [usuariosAll, setUsuariosAll] = useState<AdminUser[]>([])
  const [refreshKey, setRefreshKey] = useState(0)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  const [email, setEmail] = useState('')
  const [playerName, setPlayerName] = useState('')
  const [active, setActive] = useState<boolean | undefined>(undefined)

  const [vipOpen, setVipOpen] = useState(false)
  const [vipUser, setVipUser] = useState<AdminUser | null>(null)
  const [vipDiasVip, setVipDiasVip] = useState('30')
  const [vipMotivo, setVipMotivo] = useState('VIP Palworld gratuito')
  const [vipSubmitting, setVipSubmitting] = useState(false)
  const [vipError, setVipError] = useState<string | null>(null)
  const [snack, setSnack] = useState<string | null>(null)

  const [coinsOpen, setCoinsOpen] = useState(false)
  const [coinsUser, setCoinsUser] = useState<AdminUser | null>(null)
  const [coinsQuantidade, setCoinsQuantidade] = useState('100')
  const [coinsDescricao, setCoinsDescricao] = useState('Crédito manual (admin)')
  const [coinsSubmitting, setCoinsSubmitting] = useState(false)
  const [coinsError, setCoinsError] = useState<string | null>(null)

  const [debitOpen, setDebitOpen] = useState(false)
  const [debitUser, setDebitUser] = useState<AdminUser | null>(null)
  const [debitQuantidade, setDebitQuantidade] = useState('100')
  const [debitDescricao, setDebitDescricao] = useState('Débito manual (admin)')
  const [debitSubmitting, setDebitSubmitting] = useState(false)
  const [debitError, setDebitError] = useState<string | null>(null)

  const usuariosFiltered = useMemo(() => {
    const emailTerm = email.trim().toLowerCase()
    const playerTerm = playerName.trim().toLowerCase()

    return usuariosAll.filter((u) => {
      if (emailTerm && !(u.email ?? '').toLowerCase().includes(emailTerm)) return false
      if (playerTerm && !(u.playerName ?? '').toLowerCase().includes(playerTerm)) return false
      if (active !== undefined && u.active !== active) return false
      return true
    })
  }, [active, email, playerName, usuariosAll])

  const total = usuariosFiltered.length

  const usuariosPage = useMemo(() => {
    const start = page * rowsPerPage
    return usuariosFiltered.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, usuariosFiltered])

  useEffect(() => {
    let mounted = true
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await apiService.listarUsuariosAdmin()
        if (!mounted) return
        setUsuariosAll(normalizeUsuarios(data))
      } catch (e: any) {
        if (!mounted) return
        const msg = e?.message || 'Erro ao consultar usuários.'
        setError(msg)
      } finally {
        if (!mounted) return
        setLoading(false)
      }
    }

    run()

    return () => {
      mounted = false
    }
  }, [refreshKey])

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1)
    if (page > maxPage) setPage(0)
  }, [page, rowsPerPage, total])

  const handleSearch = () => {
    setPage(0)
    setRefreshKey((v) => v + 1)
  }

  const openVipModal = (u: AdminUser) => {
    setVipUser(u)
    setVipDiasVip('30')
    setVipMotivo('VIP Palworld gratuito')
    setVipError(null)
    setVipOpen(true)
  }

  const closeVipModal = () => {
    if (vipSubmitting) return
    setVipOpen(false)
  }

  const submitVip = async () => {
    setVipError(null)

    const userId = vipUser?.id
    if (!userId) {
      setVipError('Usuário sem ID. Não é possível conceder VIP.')
      return
    }

    const dias = Number(vipDiasVip)
    if (!Number.isFinite(dias) || !Number.isInteger(dias) || dias <= 0) {
      setVipError('Dias VIP deve ser um número inteiro maior que 0.')
      return
    }

    const motivo = vipMotivo.trim()
    if (!motivo) {
      setVipError('Motivo é obrigatório.')
      return
    }

    setVipSubmitting(true)
    try {
      await apiService.concederVipPalworldAdmin(userId, { diasVip: dias, motivo })
      setVipOpen(false)
      setSnack('VIP concedido com sucesso.')
      setRefreshKey((v) => v + 1)
    } catch (e: any) {
      setVipError(e?.message || 'Erro ao conceder VIP.')
    } finally {
      setVipSubmitting(false)
    }
  }

  const openCoinsModal = (u: AdminUser) => {
    setCoinsUser(u)
    setCoinsQuantidade('100')
    setCoinsDescricao('Crédito manual (admin)')
    setCoinsError(null)
    setCoinsOpen(true)
  }

  const closeCoinsModal = () => {
    if (coinsSubmitting) return
    setCoinsOpen(false)
  }

  const submitCoins = async () => {
    setCoinsError(null)

    const userId = coinsUser?.id
    if (!userId) {
      setCoinsError('Usuário sem ID. Não é possível creditar Warzone Coins.')
      return
    }

    const quantidade = Number(coinsQuantidade)
    if (!Number.isFinite(quantidade) || !Number.isInteger(quantidade) || quantidade <= 0) {
      setCoinsError('Quantidade deve ser um número inteiro maior que 0.')
      return
    }

    const descricao = coinsDescricao.trim()
    if (!descricao) {
      setCoinsError('Descrição é obrigatória.')
      return
    }

    setCoinsSubmitting(true)
    try {
      await apiService.creditarWarzoneCoinsAdmin({ userId, quantidade, descricao })
      setCoinsOpen(false)
      setSnack('Warzone Coins creditados com sucesso.')
      setRefreshKey((v) => v + 1)
    } catch (e: any) {
      setCoinsError(e?.message || 'Erro ao creditar Warzone Coins.')
    } finally {
      setCoinsSubmitting(false)
    }
  }

  const openDebitModal = (u: AdminUser) => {
    setDebitUser(u)
    setDebitQuantidade('100')
    setDebitDescricao('Débito manual (admin)')
    setDebitError(null)
    setDebitOpen(true)
  }

  const closeDebitModal = () => {
    if (debitSubmitting) return
    setDebitOpen(false)
  }

  const submitDebit = async () => {
    setDebitError(null)

    const userId = debitUser?.id
    if (!userId) {
      setDebitError('Usuário sem ID. Não é possível debitar Warzone Coins.')
      return
    }

    const quantidade = Number(debitQuantidade)
    if (!Number.isFinite(quantidade) || !Number.isInteger(quantidade) || quantidade <= 0) {
      setDebitError('Quantidade deve ser um número inteiro maior que 0.')
      return
    }

    const saldo = debitUser?.warzoneCoinSaldo ?? 0
    if (quantidade > saldo) {
      setDebitError('Saldo insuficiente para debitar essa quantidade.')
      return
    }

    const descricao = debitDescricao.trim()
    if (!descricao) {
      setDebitError('Descrição é obrigatória.')
      return
    }

    setDebitSubmitting(true)
    try {
      await apiService.debitarWarzoneCoinsAdmin({ userId, quantidade, descricao })
      setDebitOpen(false)
      setSnack('Warzone Coins debitados com sucesso.')
      setRefreshKey((v) => v + 1)
    } catch (e: any) {
      setDebitError(e?.message || 'Erro ao debitar Warzone Coins.')
    } finally {
      setDebitSubmitting(false)
    }
  }

  const statusChip = (isActive: boolean | undefined) => {
    if (isActive === undefined) return <Chip size="small" label="—" />
    return (
      <Chip
        size="small"
        icon={isActive ? <CheckCircle /> : <Cancel />}
        label={isActive ? 'Ativo' : 'Inativo'}
        color={isActive ? 'success' : 'error'}
      />
    )
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person />
            Gerenciar Usuários
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            Listagem de usuários do Palworld.
          </Typography>
        </Box>

        <Card>
          <CardContent sx={{ p: { xs: 3, md: 3 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nome do Jogador"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={active === undefined ? '' : String(active)}
                    label="Status"
                    onChange={(e) => {
                      const v = String(e.target.value)
                      if (!v) return setActive(undefined)
                      setActive(v === 'true')
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Ativo</MenuItem>
                    <MenuItem value="false">Inativo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button fullWidth variant="contained" startIcon={<Search />} onClick={handleSearch}>
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {error && <Alert severity="error">{error}</Alert>}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Jogador</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="right">Warzone Coins</TableCell>
                        <TableCell align="right">VIP (dias)</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usuariosPage.map((u, idx) => (
                        <TableRow
                          key={u.id ?? `${u.email ?? u.playerName ?? 'row'}-${idx}`}
                          hover
                          sx={{ cursor: u.id ? 'pointer' : 'default' }}
                          onClick={() => {
                            if (!u.id) return
                            navigate(`/admin/usuarios/${u.id}`)
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                              {u.playerName ?? '—'}
                            </Typography>
                            {u.eosid && (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                EOS: {u.eosid}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{u.email ?? '—'}</Typography>
                          </TableCell>
                          <TableCell align="center">{statusChip(u.active)}</TableCell>
                          <TableCell align="right">
                            {u.warzoneCoinSaldo === undefined ? (
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                —
                              </Typography>
                            ) : (
                              <Stack
                                direction="row"
                                spacing={0.6}
                                sx={{
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  color: u.warzoneCoinSaldo === 0 ? 'text.secondary' : 'text.primary'
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                  {u.warzoneCoinSaldo.toLocaleString('pt-BR')}
                                </Typography>
                                <Box
                                  component="img"
                                  src={WARZONE_COIN_IMAGE_URL}
                                  alt="Warzone Coin"
                                  sx={{ width: 14, height: 14, opacity: u.warzoneCoinSaldo === 0 ? 0.55 : 0.95 }}
                                  loading="lazy"
                                />
                              </Stack>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                              {u.vipDiasRestantes === undefined ? '—' : u.vipDiasRestantes.toLocaleString('pt-BR')}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
                              <Tooltip title={u.id ? 'Conceder VIP' : 'Usuário sem ID'}>
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Star fontSize="small" />}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openVipModal(u)
                                    }}
                                    disabled={!u.id}
                                  >
                                    Adicionar VIP
                                  </Button>
                                </span>
                              </Tooltip>

                              <Tooltip title={u.id ? 'Creditar Warzone Coins' : 'Usuário sem ID'}>
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Paid fontSize="small" />}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openCoinsModal(u)
                                    }}
                                    disabled={!u.id}
                                  >
                                    Adicionar Coins
                                  </Button>
                                </span>
                              </Tooltip>

                              <Tooltip title={u.id ? 'Debitar Warzone Coins' : 'Usuário sem ID'}>
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<MoneyOff fontSize="small" />}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openDebitModal(u)
                                    }}
                                    disabled={!u.id}
                                  >
                                    Debitar Coins
                                  </Button>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}

                      {usuariosPage.length === 0 && !error && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Nenhum usuário encontrado.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_e, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10))
                    setPage(0)
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={vipOpen}
        onClose={closeVipModal}
        fullWidth
        maxWidth="sm"
        BackdropProps={{ sx: vipDialogBackdropSx }}
        PaperProps={{ sx: vipDialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Conceder VIP (Palworld)</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={1.5}>
            {vipError && <Alert severity="error">{vipError}</Alert>}

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Usuário
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>
                {vipUser?.playerName || vipUser?.email || vipUser?.id || '—'}
              </Typography>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <TextField
              label="Dias VIP"
              size="small"
              value={vipDiasVip}
              onChange={(e) => setVipDiasVip(e.target.value)}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              disabled={vipSubmitting}
              fullWidth
            />

            <TextField
              label="Motivo"
              size="small"
              value={vipMotivo}
              onChange={(e) => setVipMotivo(e.target.value)}
              disabled={vipSubmitting}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeVipModal} disabled={vipSubmitting}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={submitVip} disabled={vipSubmitting}>
            {vipSubmitting ? 'Enviando...' : 'Conceder VIP'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={coinsOpen}
        onClose={closeCoinsModal}
        fullWidth
        maxWidth="sm"
        BackdropProps={{ sx: vipDialogBackdropSx }}
        PaperProps={{ sx: vipDialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Adicionar Warzone Coins</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={1.5}>
            {coinsError && <Alert severity="error">{coinsError}</Alert>}

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Usuário
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>
                {coinsUser?.playerName || coinsUser?.email || coinsUser?.id || '—'}
              </Typography>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <TextField
              label="Quantidade"
              size="small"
              value={coinsQuantidade}
              onChange={(e) => setCoinsQuantidade(e.target.value)}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              disabled={coinsSubmitting}
              fullWidth
            />

            <TextField
              label="Descrição"
              size="small"
              value={coinsDescricao}
              onChange={(e) => setCoinsDescricao(e.target.value)}
              disabled={coinsSubmitting}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeCoinsModal} disabled={coinsSubmitting}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={submitCoins} disabled={coinsSubmitting}>
            {coinsSubmitting ? 'Enviando...' : 'Creditar Coins'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={debitOpen}
        onClose={closeDebitModal}
        fullWidth
        maxWidth="sm"
        BackdropProps={{ sx: vipDialogBackdropSx }}
        PaperProps={{ sx: vipDialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Debitar Warzone Coins</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack spacing={1.5}>
            {debitError && <Alert severity="error">{debitError}</Alert>}

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Usuário
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>
                {debitUser?.playerName || debitUser?.email || debitUser?.id || '—'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Saldo atual
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>
                {(debitUser?.warzoneCoinSaldo ?? 0).toLocaleString('pt-BR')} coins
              </Typography>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <TextField
              label="Quantidade"
              size="small"
              value={debitQuantidade}
              onChange={(e) => setDebitQuantidade(e.target.value)}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              disabled={debitSubmitting}
              error={Number(debitQuantidade) > (debitUser?.warzoneCoinSaldo ?? 0)}
              helperText={
                Number(debitQuantidade) > (debitUser?.warzoneCoinSaldo ?? 0)
                  ? 'Saldo insuficiente para debitar essa quantidade.'
                  : ' '
              }
              fullWidth
            />

            <TextField
              label="Descrição"
              size="small"
              value={debitDescricao}
              onChange={(e) => setDebitDescricao(e.target.value)}
              disabled={debitSubmitting}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDebitModal} disabled={debitSubmitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={submitDebit}
            disabled={
              debitSubmitting ||
              !(Number(debitQuantidade) > 0) ||
              Number(debitQuantidade) > (debitUser?.warzoneCoinSaldo ?? 0)
            }
          >
            {debitSubmitting ? 'Enviando...' : 'Debitar Coins'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={1800}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  )
}

export default AdminUsuariosPage
